package one.healthium.onboarding.ui.components

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.text.TextMeasurer
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.unit.center
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import one.healthium.onboarding.domain.screens.OnboardingGlobalScreenConfig
import one.healthium.ui.common.utils.toColor

data class ProgressBarColors(
    val colors: List<Color>,
    val backgroundColor: Color,
)

val dotRadius = 8.dp
private val trackWidth = 6.dp

private val dotTextStyle = TextStyle(
    fontSize = 12.sp,
    fontWeight = FontWeight.Bold,
    color = Color.White,
)

object NavigationProgressBarDefaults {
    private val backgroundColor = Color(0xFFF8F6F4)
    private val colors = listOf(
        Color(0xFFDA4960),
        Color(0xFFFFB573),
        Color(0xFFFFCC4D),
        Color(0xFF96DAD5),
        Color(0xFF9BD766),
    )

    fun colors(
        colors: List<Color>? = null,
        backgroundColor: Color? = null,
    ) = ProgressBarColors(
        colors = if (colors.isNullOrEmpty()) this.colors else colors,
        backgroundColor = backgroundColor ?: this.backgroundColor,
    )
}

@Composable
fun NavigationProgressBar(
    modifier: Modifier = Modifier,
    groupsCount: Int,
    currentGroupIndex: Int,
    groupPagesCount: Int,
    groupCurrentPage: Int,
    colors: ProgressBarColors? = null,
) {
    val textMeasurer = rememberTextMeasurer()
    val resolvedColors = colors ?: NavigationProgressBarDefaults.colors()
    val backgroundColor = resolvedColors.backgroundColor

    Canvas(modifier.fillMaxWidth()) {
        val dotSize = dotRadius.times(2).toPx()
        val dotsGap = (size.width - dotSize) / (groupsCount)
        val dotCurveCompensationOffset = 1.dp.toPx()

        repeat(groupsCount + 1) { index ->
            val activeColor = resolvedColors.colorByIndex(index)
            val isActive = index <= currentGroupIndex

            if (index < groupsCount) {
                val trackY = dotRadius.toPx()
                val nextActiveColor = resolvedColors.colorByIndex(index + 1)

                drawTrack(
                    // current dot position
                    start = Offset(
                        x = (index * dotsGap) + dotSize - dotCurveCompensationOffset,
                        y = trackY,
                    ),
                    // next dot position
                    end = Offset(
                        x = ((index + 1) * dotsGap) + dotCurveCompensationOffset,
                        y = trackY,
                    ),
                    backgroundColor = backgroundColor,
                    startColor = activeColor,
                    endColor = nextActiveColor,
                    fraction = when {
                        index < currentGroupIndex -> 1f
                        index == currentGroupIndex -> groupCurrentPage / groupPagesCount.toFloat()
                        else -> 0f
                    }
                )
            }

            drawDot(
                text = if (isActive) "${index + 1}" else "",
                textMeasurer = textMeasurer,
                color = if (isActive) activeColor else backgroundColor,
                topLeft = Offset(index * dotsGap, 0f),
            )
        }
    }
}

private fun DrawScope.drawDot(
    text: String,
    textMeasurer: TextMeasurer,
    color: Color,
    topLeft: Offset
) {
    val center = topLeft.plusOffset(dotRadius.toPx())
    drawCircle(
        color = color,
        radius = dotRadius.toPx(),
        center = center,
    )

    if (text.isNotBlank()) {
        val measuredText = textMeasurer.measure(
            text = text,
            style = dotTextStyle,
        )
        drawText(
            textLayoutResult = measuredText,
            topLeft = center.minus(measuredText.size.center.x, measuredText.size.center.y)
        )
    }
}

private fun DrawScope.drawTrack(
    start: Offset,
    end: Offset,
    backgroundColor: Color,
    startColor: Color,
    endColor: Color,
    fraction: Float,
) {
    val strokeWidth = trackWidth.toPx()
    drawLine(
        // TODO
//        brush = Brush.horizontalGradient(
//            listOf(startColor, endColor),
//            startX = 0f,
//            endX = end.x - start.x,
//        ),
        color = startColor,
        start = start.minus(x = 1),
        end = end,
        strokeWidth = strokeWidth,
    )
    drawLine(
        color = backgroundColor,
        start = start.plus(x = (end.x - start.x) * fraction),
        end = end,
        strokeWidth = strokeWidth + 1.5f,
    )
}

private fun Offset.plusOffset(offset: Float): Offset {
    return this.copy(
        x = x + offset,
        y = y + offset,
    )
}

private fun Offset.plus(x: Float = 0f, y: Float = 0f): Offset {
    return this.copy(
        x = this.x + x,
        y = this.y + y,
    )
}

private fun Offset.minus(x: Int = 0, y: Int = 0): Offset {
    return this.copy(
        x = this.x - x,
        y = this.y - y,
    )
}

private fun ProgressBarColors.colorByIndex(index: Int): Color {
    return this.colors.getOrElse(index) { this.colors.last() }
}

@Composable
fun OnboardingGlobalScreenConfig.toProgressBarModel(): ProgressBarColors {
    return NavigationProgressBarDefaults.colors(
        colors = this.progressColors.map { it.toColor() ?: Color.White },
        backgroundColor = this.progressBackground?.toColor(),
    )
}